/**
 * EventEntryModal — choose which activities to enter, then enter them.
 *
 * It opens on Register (or on the entry form's own submit, with the answers
 * already in hand) and it is the last thing between a visitor and a written
 * registration. Four states, in order: CHOOSE, CHECKING, WORKING, REPORT.
 *
 * WHAT CHECKING IS FOR. `max_participants` is the size of the room, not the
 * number of empty chairs, so the chooser used to offer seven seats on an
 * activity with three left — writing five and reporting the rest as refused, a
 * correct outcome delivered in the worst possible order. CHECKING asks what is
 * actually left, trims the selection to it, and says what it changed, all
 * before anything is written or charged. It is a courtesy, not a gate: it is
 * skipped when no checker is wired up, and a checker that cannot answer is
 * treated as one that was never there. Capacity is still enforced inside the
 * write, which is the only place a race can be lost.
 *
 * WHY A LINE PER ACTIVITY IN THE REPORT. Each ticked activity is its own write,
 * checked against its own capacity and against what this person already holds,
 * so they genuinely can end differently: the 10K saves and the yoga comes back
 * full. Collapsing that into one "something went wrong" would hide a
 * registration that actually succeeded, and a visitor who retries would be told
 * they had already registered — for a thing they were never told they had.
 *
 * MOUNTED AT THE DOCUMENT ROOT, INSIDE ITS OWN `.evb`. The portal escapes the
 * page's stacking contexts — the sticky rail, the map panes, the entry band —
 * which a fixed overlay nested among them cannot reliably rise above. But the
 * page's colour tokens live ON `.evb`, so a portal to <body> lands outside them
 * and every var(--b-*) resolves to nothing: a panel with no background, no
 * borders, and the page showing straight through it. The wrapper carries the
 * tokens back, which is why it is not simply document.body.
 *
 * SEATS ARE ROWS. Capacity is counted in rows against the activity's own
 * `max_participants`, so three seats means three registrations and three
 * passes — not one row holding a number, which would count as one place and
 * let the activity oversell.
 *
 * EVERY PRESS OF CONTINUE MINTS ONE CHECKOUT ID. It travels with every seat of
 * that press, alongside the seat's own number, and the database holds a unique
 * rule over the pair. That is what tells "Asha's second seat" apart from
 * "Asha's first seat, sent twice" — two facts that are otherwise byte-identical
 * rows. It is why a dropped reply cannot become a phantom entry with a real QR
 * against it.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EventPassViewer from './EventPassViewer';
import {
  seatFee, isPaidCheckout, checkoutTotal, REGISTRATION_ENTITY_CODE,
} from './buildCheckoutPayload';
import { validateCoupon } from './checkoutClient';
import useCheckoutQuote from './useCheckoutQuote';
import CheckoutSummary from './CheckoutSummary';
import { requestIdempotencyKey } from './checkoutClient';
import {
  DEFAULT_DIAL_CODE, dialCodeEntry, normaliseNumber, validateNumber,
} from './countryDialCodes';
import { attendeeInfoOf, maxSeatsPerCheckout } from './buildRegistrationBatch';

/** The gender choices a participant row offers — the profile's own codes. */
const GENDER_CHOICES = [
  { value: 'FEMALE', label: 'Female' },
  { value: 'MALE', label: 'Male' },
  { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
];

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The consent every entry must tick before the button unlocks.
export const CONSENT_TEXT = 'I agree to the terms and conditions, understand the health risks '
  + 'associated with fitness activities, and consent to AthloHive using my information for '
  + 'communications and promotional purposes.';

/**
 * What is wrong with one participant row, field by field — for hints only.
 * Nothing here ever disables Continue or Pay: every participant field is
 * optional, and a malformed one is simply not saved (see attendeeInfoOf).
 */
/**
 * The server's reason code, as a sentence for the runner.
 *
 * Each refusal says something different and they must not collapse into one
 * message: "already used" and "fully claimed" send the runner to different
 * next actions. Anything unrecognised falls back to neutral wording rather
 * than leaking a reason code into the UI.
 */
export function couponRefusal(reason) {
  switch (reason) {
    case 'unknown_code':
      return 'Invalid code. Please check the code and try again.';
    case 'already_used_by_user':
      return 'You have already used this code on an earlier entry.';
    case 'fully_claimed':
      return 'This offer has been fully claimed. Thank you for your interest.';
    case 'expired':
      // Softened on purpose: the runner did nothing wrong, and a bare
      // "expired" reads as a telling-off for being late.
      return 'This offer has now ended. Please continue without a code, or try another one.';
    case 'not_yet_valid':
      return 'This offer has not started yet. Please try again once it opens.';
    case 'code_inactive':
    case 'discount_inactive':
      return 'This code is no longer available.';
    case 'exceeds_total':
      return 'This code is worth more than this entry costs, so it cannot be applied here.';
    case 'not_valid_for_these_items':
      return 'This code is not valid for this event.';
    case 'nothing_to_discount':
      return 'There is nothing to discount on this entry.';
    case 'no_user':
      return 'Please sign in again before using a code.';
    default:
      return 'That code cannot be applied to this entry.';
  }
}


function personProblems(person, countryCode) {
  const problems = {};
  const email = String(person?.email ?? '').trim();
  if (email && !EMAIL_SHAPE.test(email)) problems.email = 'This email looks incomplete.';
  const mobile = String(person?.mobile_number ?? '');
  if (mobile && !validateNumber(countryCode, mobile).valid) {
    problems.mobile_number = 'Check the mobile number.';
  }
  const age = String(person?.age ?? '').trim();
  if (age && !(/^\d{1,2}$/.test(age) && Number(age) >= 1)) problems.age = 'Age should be 1 to 99.';
  return problems;
}

/*
 * The previous cap: ten seats per ACTIVITY, with no view of the cart as a
 * whole. Kept here because it explains what replaced it and why there are now
 * two numbers instead of one.
 *
 * It was wrong in both directions at once. Ten was far below what one booking
 * can hold, and a per-activity ceiling cannot see the batch it is part of: ten
 * on each of three activities is thirty seats, sixty operations, and a server
 * refusal the stepper had already promised would be fine.
 */
// const SEATS_PER_CHECKOUT = 10;

/**
 * The most seats one ACTIVITY may take in a single booking.
 *
 * A product decision, not a technical ceiling — the batch could hold all of
 * these on one activity (the real boundary is the cart ceiling, derived from
 * the batch operation cap). It is stated as its own number so that changing
 * the rule is a
 * one-line edit that cannot accidentally be read as changing what the server
 * will accept.
 */
const SEATS_PER_ACTIVITY = 25;

/**
 * When "how many are left" stops being reassurance and becomes urgency.
 *
 * Deliberately NOT the seat cap, though one constant used to serve as both.
 * The cap answers "how many may I take"; this answers "is this nearly gone",
 * and they are different questions with different right answers. Tying the
 * urgency text to the cap meant that raising the cap made the page announce
 * "only 24 places left" about an activity with plenty of room.
 */
const FEW_LEFT = 10;

/**
 * The activity's own capacity, or undefined when it does not declare one.
 *
 * Shared by the ceiling and the message that explains it, so "this activity is
 * small" is decided the same way in both places rather than inferred twice.
 */
function capacityOf(activity) {
  const capacity = Number(activity?.max_participants);
  return Number.isFinite(capacity) && capacity > 0 ? capacity : undefined;
}

/**
 * The ceiling for one activity's stepper: the smallest of the per-activity
 * rule, the activity's capacity, what is actually left once it has been asked
 * for, and what the rest of the cart has not already spent.
 *
 * `remaining` is undefined until the availability check has answered for this
 * activity, and stays undefined when it could not. Undefined is not zero: an
 * unanswered question must leave the ceiling exactly where it was before this
 * argument existed, which is what keeps a refused or unreachable check from
 * quietly closing an event that has room.
 *
 * `headroom` is the SAME kind of value and gets the same treatment — a caller
 * that does not know the cart's budget passes nothing and the ceiling is
 * decided without it.
 *
 * Zero IS possible, and means the activity is full. It is the one case that
 * returns a ceiling below one, because "choose at least one seat" stops being
 * true the moment there are none.
 */
function seatCeiling(activity, remaining, headroom) {
  const capacity = capacityOf(activity);
  const limits = [SEATS_PER_ACTIVITY];
  if (capacity !== undefined) limits.push(capacity);
  if (Number.isFinite(remaining)) limits.push(remaining);
  if (Number.isFinite(headroom)) limits.push(headroom);
  return Math.max(0, Math.min(...limits));
}

/**
 * A stable fingerprint of one selection — what "this cart has already been
 * checked" is remembered as.
 *
 * Sorted, because a Map's order follows the order things were ticked: picking
 * the 10K then the yoga and picking them the other way round are the same cart
 * and must not be checked twice.
 */
function cartSignature(seats) {
  return Array.from(seats.entries())
    .map(([id, count]) => `${id}:${count}`)
    .sort()
    .join('|');
}

/** One id per press of Continue — the key the database dedupes on. */
function newCheckoutId() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (e) {
    // Falls through to the timestamp form below.
  }
  return `c${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
}

const CHOOSE = 'choose';
/** Asking what is left, before anything is written. Nothing has been sent yet. */
const CHECKING = 'checking';
const WORKING = 'working';
const REPORT = 'report';

/**
 * How long the seat selection must hold still before an idempotency key is
 * minted for it. Long enough to absorb a run of stepper presses, short enough
 * that the key is waiting by the time anyone reaches the Pay button.
 */
const KEY_MINT_DEBOUNCE_MS = 400;

/** Everything the server said, in the order it is worth reading. */
function failureText(result) {
  const detail = Array.isArray(result.detail)
    ? result.detail.join(' ')
    : (typeof result.detail === 'string' ? result.detail : '');
  return [result.message, detail].filter(Boolean).join(' — ');
}

export default function EventEntryModal({
  open, onClose, activities, formatFee, eventTitle, onConfirm,
  // When the visitor can check themselves in, shown under the passes once a
  // seat is booked. See checkInOpensHint. '' or absent draws nothing.
  checkInHint,
  // Prefill for the contact fields a paid checkout needs. Read-only: whatever
  // the runner types pays for THIS checkout and is never written back, because
  // entering on a borrowed phone must not rewrite the account.
  contact,
  applicationId,
  // Which event these seats belong to. Needed so a discount scoped to one
  // event can be refused on another while the runner is still choosing,
  // rather than at the Pay button.
  eventId,
  /**
   * Asks how many places each picked activity still has, as
   * `(activities) => Promise<Map<id, remaining> | null>`.
   *
   * OPTIONAL, AND THAT IS THE POINT. Left out, this modal behaves exactly as it
   * did before the check existed: the server is still the only thing that
   * decides whether a seat can be written, and the per-seat report is still how
   * a refusal is explained. The check narrows the window in which a visitor can
   * ask for seats that are not there; it never becomes the thing standing
   * between them and an entry.
   *
   * Null — or a throw, or a map with nothing in it — means "could not tell",
   * and is treated as though no check were configured at all.
   */
  onCheckAvailability,
  /**
   * Offer "Add participant details" under any activity with 2+ seats.
   *
   * Off unless asked for: only a seat that gets a pass has somewhere to put a
   * name, so the page turns this on for events that mint passes and leaves it
   * off for a waiting list.
   */
  collectParticipants = false,
  /**
   * Whether a seat on this event mints a pass.
   *
   * It decides the CART ceiling, not anything visible: a seat with a pass is
   * two writes and one without is one, so the same operation budget holds half
   * as many of the former. Defaulting to false would quietly double the
   * ceiling on a page that forgot to pass it, so the page sends
   * `rules.mintsPass` and the batch builder refuses anything that slips past.
   */
  mintsPass = false,
}) {
  const items = Array.isArray(activities) ? activities.filter(a => a && a.id != null) : [];

  /**
   * The most seats this whole checkout may hold, across every activity.
   *
   * THE BACKSTOP, not the rule a visitor meets — that is SEATS_PER_ACTIVITY.
   * With three activities at twenty-five each the two numbers coincide exactly,
   * so this never binds and the stepper reads as a clean per-activity limit. A
   * fourth activity is where it earns its place: four times twenty-five is more
   * operations than the batch may carry, and this is what says so in the modal
   * instead of letting the server refuse a cart it already accepted.
   */
  const cartCap = maxSeatsPerCheckout(mintsPass);

  // activity id -> seat count. Absent means not chosen; chosen always means 1+.
  const [seats, setSeats] = useState(() => new Map());
  const [phase, setPhase] = useState(CHOOSE);
  const [results, setResults] = useState([]);
  const [fatal, setFatal] = useState(null);
  // Read by the came-back-from-the-gateway listeners below. They are
  // registered once, so closing over `phase` would pin them to its first
  // value and the check would never fire.
  const phaseRef = useRef(CHOOSE);
  const panelRef = useRef(null);

  // What the check last said: activity id -> places left. Null until it has
  // answered, and null again for an answer it could not give. An activity
  // missing from the map is one whose remaining places are not known — which is
  // not the same as an activity with none, and must not render as one.
  const [availability, setAvailability] = useState(null);

  // What the check had to change about the selection, in the visitor's words.
  // Held rather than derived because it describes a MOMENT — what was asked for
  // versus what was there — and the seats it refers to have already been
  // rewritten by the time it is read.
  const [trimNotice, setTrimNotice] = useState(null);

  /**
   * The signature of the selection the check has already settled.
   *
   * Without it, the press after a trim would ask again, get the same answer,
   * change nothing, and still cost the visitor a round trip before their entry
   * — a second press that looks exactly like the first one failing. With it,
   * the second press writes. Any edit to the cart changes the signature and
   * earns a fresh check, which is the case where asking again is the point.
   */
  const verifiedRef = useRef(null);

  /**
   * Which opening of the modal we are in.
   *
   * The check is awaited, and closing the modal mid-check is allowed — nothing
   * has been written at that point, so there is no reason to trap anyone. But
   * the awaited call still resolves afterwards, and without this it would walk
   * on into the write: a visitor who closed the window would get an entry they
   * cancelled. The id is compared across the await; a mismatch drops the answer.
   */
  const runIdRef = useRef(0);

  // Contact details, prefilled and editable. Only asked for when money is
  // involved -- a free entry has no reason to want a phone number.
  const [countryCode] = useState(DEFAULT_DIAL_CODE);
  const [mobileNumber, setMobileNumber] = useState('');
  // Set by pressing Pay without a usable number. Pay stays pressable so the
  // press can say what is missing; this lets the field complain before
  // anything has been typed into it.
  const [phoneAsked, setPhoneAsked] = useState(false);
  const mobileRef = useRef(null);

  // Terms / health-risk / communications consent. Required for every entry,
  // free or paid, and never stored: ticking it is what unlocks the button.
  const [agreed, setAgreed] = useState(false);

  // The coupon the runner typed, and what the server said it is worth. Kept
  // apart on purpose: `couponInput` is what is in the box, `couponApplied` is
  // what has actually been priced. Editing the box clears the second, so a
  // stale discount can never be shown against a code nobody checked.
  const [couponInput, setCouponInput] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponNote, setCouponNote] = useState('');
  // The cart the applied discount was priced against. A saving worked out
  // for two seats is simply wrong for three, so this is what the effect
  // below compares to decide the figure on screen has gone stale.
  const appliedForRef = useRef(null);

  // Who each seat is for, when the booker says: activity id -> one row per
  // seat, in seat order. Nothing is prefilled — an empty row means "me", which
  // is what every seat meant before this existed.
  const [participants, setParticipants] = useState(() => new Map());
  // Activities whose participant rows are showing. Closed until asked for.
  const [peopleOpen, setPeopleOpen] = useState(() => new Set());
  // Rows whose optional gender and age are showing, as "activityId:index".
  const [rowMore, setRowMore] = useState(() => new Set());

  /**
   * THE KEY FOR THIS SELECTION. Held in a ref, not state, and deliberately not
   * minted inside confirm().
   *
   * Two failures sit on either side of this one value. Mint it per attempt and
   * a retry after a dropped connection opens a SECOND gateway order for the
   * same press. Hold it across a cart change and the server replays the
   * original payment -- the page shows the new total while the gateway charges
   * the old one. So: one key per selection, reused for every retry of that
   * selection, dropped the instant the selection changes.
   */
  const keyRef = useRef(null);
  const [keyReady, setKeyReady] = useState(false);

  // A fresh choice each time it opens — reopening with the previous ticks still
  // set would invite a second submission of what was just refused.
  useEffect(() => {
    if (!open) return;
    setSeats(new Map(items.length === 1 ? [[items[0].id, 1]] : []));
    setPhase(CHOOSE);
    setResults([]);
    setFatal(null);
    keyRef.current = null;
    setKeyReady(false);
    // What was left a moment ago is not what is left now, and a stale count is
    // worse than none: it would cap a stepper against places that have since
    // been freed, or offer ones that have since gone.
    setAvailability(null);
    setTrimNotice(null);
    verifiedRef.current = null;
    setMobileNumber(normaliseNumber(contact?.mobile_number));
    setPhoneAsked(false);
    setAgreed(false);
    setParticipants(new Map());
    setPeopleOpen(new Set());
    setRowMore(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Rows follow the seats: fewer seats drops the rows past the new count, an
  // unticked activity (or one the availability check emptied) drops them all.
  // More seats needs nothing here — a missing row reads as an empty one.
  // Kept in step with `phase` so the listeners below can read it without
  // being re-registered on every phase change.
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  /**
   * COMING BACK FROM THE GATEWAY WITHOUT HAVING PAID.
   *
   * Pressing Pay leaves the site, so this component is frozen mid-flight with
   * `phase` still WORKING. Press the browser's back button and the browser
   * restores it from its cache exactly as it was -- spinner turning, "Taking
   * you to payment…" -- for a payment that is not happening and never will be
   * in this tab. The runner is left watching a promise that cannot be kept.
   *
   * `pageshow` with `persisted` is the browser saying "I restored this from
   * cache rather than loading it", which is precisely the back-button case.
   * `visibilitychange` catches the rest: a mobile browser returning from an
   * app switch, a tab brought back to the front.
   *
   * Reported as a failure, not as a question. Nothing was charged -- the
   * gateway was never completed -- so the honest thing is to say so and leave
   * the choice intact so they can simply press Pay again. Their places are
   * still reserved for a few moments, and the server gives them back on its
   * own if this really was a cancellation.
   */
  useEffect(() => {
    if (!open) return undefined;

    const cameBack = (restoredFromCache) => {
      // Only meaningful mid-handover. Any other phase is either still being
      // filled in or already finished, and neither is a failure.
      if (phaseRef.current !== WORKING) return;
      if (!restoredFromCache && document.visibilityState !== 'visible') return;
      setPhase(CHOOSE);
      setFatal(
        'That payment was not completed, and nothing has been charged. '
        + 'Your places are still held for a short while — press Pay to try again.'
      );
    };

    const onPageShow = (e) => cameBack(Boolean(e && e.persisted));
    const onVisible = () => cameBack(false);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [open]);

  useEffect(() => {
    setParticipants(current => {
      let changed = false;
      const next = new Map();
      current.forEach((rows, id) => {
        const count = seats.get(id) || 0;
        if (count < 2) { changed = true; return; }
        if (rows.length > count) { changed = true; next.set(id, rows.slice(0, count)); return; }
        next.set(id, rows);
      });
      return changed ? next : current;
    });
  }, [seats]);

  // Fetch the key while the runner is still choosing, not when they press Pay.
  // The round trip is then free: it overlaps the part of the flow where they
  // are reading, rather than the part where they are waiting on a button.
  //
  // Only for a paid selection -- a free entry never reaches checkout, so
  // asking for a key would spend one for nothing.
  //
  // SETTLED FIRST, THEN MINTED. Every seat change calls forgetKey(), so this
  // effect re-runs on each one and used to POST a key per keystroke of the
  // stepper — going from one seat to five minted five keys and abandoned four.
  // The short wait below lets a burst of +/- presses settle into one request.
  // It costs nothing at the point it matters: the runner still has the contact
  // step ahead of them, and Pay stays disabled until keyReady, exactly as
  // before.
  useEffect(() => {
    if (!open) return undefined;
    const chosen = items.filter(a => seats.has(a.id));
    const wantsMoney = chosen.some(a => seatFee({ activity: a }) > 0);
    if (!wantsMoney || keyRef.current) return undefined;

    let cancelled = false;
    const timer = setTimeout(() => {
      requestIdempotencyKey({ applicationId }).then(key => {
        // A key that arrives after the cart changed again is dropped: the effect
        // has already been re-run for the new selection, and adopting this one
        // would attach a key minted for the previous cart.
        if (cancelled || !key) return;
        keyRef.current = key;
        setKeyReady(true);
      });
    }, KEY_MINT_DEBOUNCE_MS);

    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [open, seats, applicationId]);
  // `participants` too: a participant edit drops the key (setPerson), and
  // without this nothing would mint the next one and Pay would stay disabled.
  }, [open, seats, participants, applicationId]);

  // Every open and every close is its own run. Bumped in both directions
  // precisely because closing is the case that matters: see runIdRef.
  useEffect(() => { runIdRef.current += 1; }, [open]);

  // Escape closes it — but never mid-write, where closing would leave the
  // visitor unsure whether their entry went through.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = event => {
      if (event.key === 'Escape' && phase !== WORKING) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, phase, onClose]);

  useEffect(() => {
    if (open && panelRef.current) panelRef.current.focus();
  }, [open]);

  /**
   * Re-prices an applied code when the seats change underneath it.
   *
   * Without this the saving sticks at whatever the cart was worth when Apply
   * was pressed: add a third seat to a 20%-off cart and the discount stays
   * on two, so the total charged is not 20% of anything the runner can see.
   *
   * The server is asked again rather than the page multiplying it out — the
   * ceiling and the scope rules are the server's to apply, and a page that
   * computed its own figure would eventually disagree with the charge.
   *
   * Lives ABOVE the `!open` return with the other hooks: a hook after an
   * early return changes the hook order between renders. The seat list is
   * therefore rebuilt here from `items` and `seats` rather than reusing
   * `chosenSeats`, which is derived further down.
   */
  useEffect(() => {
    if (!open || !couponApplied || couponBusy) return undefined;

    const signature = cartSignature(seats);
    if (signature === appliedForRef.current) return undefined;

    const chosen = items
      .filter(activity => seats.has(activity.id))
      .flatMap(activity => Array.from(
        { length: seats.get(activity.id) || 1 },
        () => ({ activity }),
      ));

    // An emptied cart is not a failed re-check. Asking the server to price a
    // code against nothing earns a 400, and the runner was then told "we
    // could not re-check that code" for the crime of unticking a box. The
    // discount is simply dropped -- there is nothing left for it to act on --
    // and the box keeps the code so one tick puts it back.
    if (chosen.length === 0) {
      appliedForRef.current = null;
      setCouponApplied(null);
      setCouponNote('');
      return undefined;
    }

    // Marked as priced for this cart BEFORE the round trip, so a second seat
    // added while the first call is still out does not start a third.
    appliedForRef.current = signature;

    let cancelled = false;
    const code = couponApplied.code;

    (async () => {
      const answer = await validateCoupon({
        couponCode: code,
        items: chosen.map(seat => ({
          amount: String(seatFee(seat)),
          chargeable_entity_code: REGISTRATION_ENTITY_CODE,
          chargeable_entity_parent_id: eventId,
        })),
      });
      if (cancelled) return;
      if (!answer || !answer.applicable) {
        // The code no longer fits this cart -- too small for a flat discount,
        // say. Dropped rather than left showing a saving that is not real.
        setCouponApplied(null);
        setCouponNote(answer
          ? couponRefusal(answer.reason)
          : 'We could not re-check that code. Please apply it again.');
        return;
      }
      setCouponApplied({ ...answer, code });
    })();

    return () => { cancelled = true; };
  }, [open, couponApplied, couponBusy, seats, items, eventId]);

  /**
   * The cart as the quote endpoint wants it — one entry per seat.
   *
   * Built HERE, above the `!open` early return, for the same reason the
   * re-pricing effect above rebuilds its own list: the hook below is a hook,
   * and a hook after an early return changes the hook order between renders.
   *
   * Rebuilt fresh on every render rather than memoised, deliberately. The hook
   * keys on this array's serialised CONTENT, so a new array holding the same
   * seats is not a new question and costs nothing — whereas a memo would need
   * a dependency list that drifts out of step with what is actually in the
   * cart.
   *
   * The same per-seat shape the coupon preview sends, and the same shape
   * buildCheckoutPayload will send, so the quote, the preview and the charge
   * are all answering one question.
   */
  const quoteItems = items
    .filter(activity => seats.has(activity.id))
    .flatMap(activity => Array.from(
      { length: seats.get(activity.id) || 1 },
      () => ({
        // A free seat is sent AT ZERO rather than dropped, exactly as the
        // checkout sends it: which seats are exempt and which are merely free
        // is the server's call, and a page that filtered one out would be
        // making that call itself.
        amount: String(seatFee({ activity })),
        chargeable_entity_code: REGISTRATION_ENTITY_CODE,
        chargeable_entity_parent_id: eventId,
      }),
    ));

  const { quote, quoteBusy, quoted } = useCheckoutQuote({
    // Asked for only when there is money in the cart. A free entry has no tax
    // to look up, and a round trip to be told so is a round trip spent to
    // learn nothing.
    enabled: open && quoteItems.some(item => Number(item.amount) > 0),
    items: quoteItems,
    // The code the server has actually priced, not what is in the box — the
    // taxable value is the discounted one, so an unapplied code must not move
    // it. Mirrors how `discount` below is read from couponApplied.
    couponCode: couponApplied ? couponApplied.code : null,
    applicationId,
  });

  if (!open) return null;

  // forgetKey is called from inside the seat updaters rather than from an
  // effect that runs afterwards: an effect leaves a window where the selection
  // is new and the key is still the old one, which is exactly the
  // charge-the-wrong-cart case.
  const forgetKey = () => { keyRef.current = null; setKeyReady(false); };

  /**
   * What the check said is left for this activity, or undefined.
   *
   * Undefined for every activity until the check has answered, and for any
   * activity it could not answer about — so every reading below degrades to the
   * behaviour that was there before the check existed.
   */
  const leftFor = (activity) => (
    availability && availability.has(activity?.id) ? availability.get(activity.id) : undefined
  );

  /**
   * Seats already spoken for by every activity EXCEPT this one.
   *
   * Takes the map explicitly because the two callers below run inside a
   * setSeats updater, where the render's `seats` is the value being replaced.
   * Reading the stale one there would let two quick presses on different
   * activities each believe the other had not spent anything.
   */
  const spentElsewhere = (activityId, from) => {
    let total = 0;
    from.forEach((count, id) => {
      if (id !== activityId) total += count || 1;
    });
    return total;
  };

  /**
   * This activity's stepper ceiling, against whatever is currently known.
   *
   * `from` defaults to the rendered selection, which is what the stepper and
   * its disabled state want. The updaters pass their own `current` instead.
   */
  const ceilingFor = (activity, from = seats) => seatCeiling(
    activity, leftFor(activity), headroomFor(activity, from),
  );

  /**
   * What the booking's own budget leaves for this activity.
   *
   * Named separately from the ceiling because the two kinds of "full" need
   * different sentences: an activity with no places left is a dead end, and a
   * booking with no budget left is fixed by taking a seat off something else.
   * Telling a visitor "no places left" about an activity with eight hundred of
   * them is worse than saying nothing.
   */
  function headroomFor(activity, from = seats) {
    return cartCap - spentElsewhere(activity?.id, from);
  }

  const toggle = (id) => setSeats(current => {
    // A full activity has nothing to tick. The checkbox is already disabled, so
    // this only catches a click that reached the handler anyway — a label press
    // in a browser that does not honour the attribute, or a stale render.
    if (ceilingFor(items.find(a => a.id === id), current) <= 0) return current;
    forgetKey();
    const next = new Map(current);
    if (next.has(id)) next.delete(id); else next.set(id, 1);
    return next;
  });

  // Takes a DELTA and reads the count inside the update, never from the render
  // that drew the button. Reading it outside made three quick presses of + all
  // see the same value and write the same result — the seat count stopped at 2
  // however many times it was pressed.
  //
  // Never below one: a chosen activity with no seats is a contradiction, and
  // zero is what the checkbox is for.
  const bumpSeats = (id, delta) => setSeats(current => {
    if (!current.has(id)) return current;
    forgetKey();
    const activity = items.find(a => a.id === id);
    const next = new Map(current);
    next.set(id, Math.min(ceilingFor(activity, current),
                          Math.max(1, (current.get(id) || 1) + delta)));
    return next;
  });

  // One participant field changed. The key goes too: a paid checkout's rows
  // carry these details, and the server refuses a key reused for a different
  // body — the same reason a seat change drops it.
  const setPerson = (activityId, index, field, value) => {
    forgetKey();
    setParticipants(current => {
      const next = new Map(current);
      const rows = (next.get(activityId) || []).slice();
      rows[index] = { ...(rows[index] || {}), [field]: value };
      next.set(activityId, rows);
      return next;
    });
  };

  const setPeopleShown = (activityId, shown) => setPeopleOpen(current => {
    const next = new Set(current);
    if (shown) next.add(activityId); else next.delete(activityId);
    return next;
  });

  const toggleRowMore = (key) => setRowMore(current => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  /** How many of this activity's seats have a participant named. */
  const namedCount = (activityId, count) => {
    const rows = participants.get(activityId) || [];
    let named = 0;
    for (let i = 0; i < count; i += 1) if (attendeeInfoOf(rows[i])) named += 1;
    return named;
  };

  /** The summary under the rows: how many are named, and who the rest are. */
  const peopleSummary = (named, count) => {
    if (named === 0) return `Nothing added yet. All ${count} passes will be in your name.`;
    if (named === count) {
      return 'All seats are for others, so no pass will be in your name. '
        + 'That’s fine if you’re not taking part.';
    }
    const rest = count - named;
    return `${named} of ${count} added. The other ${rest} ${rest === 1 ? 'stays' : 'stay'} in your name.`;
  };

  /**
   * "Add participant details" for one activity, and the rows behind it.
   *
   * Closed by default so a booking that does not care looks exactly as it did.
   * Hiding keeps what was typed; the button then says how many are added.
   */
  function renderPeople(activity, count) {
    const id = activity.id;
    const rows = participants.get(id) || [];
    const shown = peopleOpen.has(id);
    const named = namedCount(id, count);
    const panelId = `evb-people-${id}`;

    if (!shown) {
      return (
        <button
          type="button"
          className="evb-people__add"
          onClick={() => setPeopleShown(id, true)}
          disabled={busy}
          aria-expanded="false"
          aria-controls={panelId}
        >
          <span className="evb-people__plus" aria-hidden="true">+</span>
          <span>
            {named ? 'Edit participant details' : 'Add participant details'}
            <span className="evb-people__opt"> (optional)</span>
          </span>
          {named > 0 && (
            <span className="evb-people__sum">{named} of {count} added</span>
          )}
        </button>
      );
    }

    return (
      <div className="evb-people" id={panelId}>
        <div className="evb-people__head">
          <div>
            <p className="evb-people__title">
              Who&rsquo;s taking these seats? <span className="evb-people__opt">(optional)</span>
            </p>
            <p className="evb-people__lead">
              Add your friends&rsquo; details so each pass carries their name at check-in.
              Any row you leave empty stays in your name.
            </p>
          </div>
          <button
            type="button"
            className="evb-people__hide"
            onClick={() => setPeopleShown(id, false)}
            disabled={busy}
          >
            Hide
          </button>
        </div>

        {Array.from({ length: count }, (_, index) => {
          const person = rows[index] || {};
          const done = Boolean(attendeeInfoOf(person));
          const problems = personProblems(person, countryCode);
          const moreKey = `${id}:${index}`;
          const more = rowMore.has(moreKey);
          const fid = field => `evb-p-${id}-${index}-${field}`;
          const n = index + 1;
          const hints = [problems.email, problems.mobile_number, more ? problems.age : null]
            .filter(Boolean);
          const startedWithoutName = !done && ['last_name', 'email', 'mobile_number', 'gender', 'age']
            .some(field => String(person[field] ?? '').trim() !== '');
          return (
            <div className={`evb-person${done ? ' evb-person--done' : ''}`} key={index}>
              <div className="evb-person__head">
                <span>Person {n}</span>
                <span className="evb-person__state">
                  {done ? '✓ Added' : 'Empty · stays in your name'}
                </span>
              </div>
              <div className="evb-person__row">
                <input
                  id={fid('first')} className="evb-person__input" type="text"
                  autoComplete="off" placeholder="First name" aria-label={`Person ${n} first name`}
                  maxLength={50}
                  value={person.first_name || ''} disabled={busy}
                  onChange={e => setPerson(id, index, 'first_name', e.target.value)}
                />
                <input
                  id={fid('last')} className="evb-person__input" type="text"
                  autoComplete="off" placeholder="Last name" aria-label={`Person ${n} last name`}
                  maxLength={50}
                  value={person.last_name || ''} disabled={busy}
                  onChange={e => setPerson(id, index, 'last_name', e.target.value)}
                />
              </div>
              <div className="evb-person__row">
                <input
                  id={fid('email')} className="evb-person__input" type="email"
                  autoComplete="off" placeholder="Email" aria-label={`Person ${n} email`}
                  maxLength={254}
                  aria-invalid={Boolean(problems.email)}
                  value={person.email || ''} disabled={busy}
                  onChange={e => setPerson(id, index, 'email', e.target.value)}
                />
                <input
                  id={fid('mobile')} className="evb-person__input" type="tel" inputMode="numeric"
                  autoComplete="off" placeholder="Mobile" aria-label={`Person ${n} mobile`}
                  aria-invalid={Boolean(problems.mobile_number)}
                  maxLength={normaliseNumber(dialCodeEntry(countryCode).sample).length}
                  value={person.mobile_number || ''} disabled={busy}
                  onChange={e => setPerson(id, index, 'mobile_number', normaliseNumber(e.target.value))}
                />
              </div>
              {more && (
                <div className="evb-person__row evb-person__row--ga">
                  <select
                    id={fid('gender')} className="evb-person__input"
                    aria-label={`Person ${n} gender`}
                    value={person.gender || ''} disabled={busy}
                    onChange={e => setPerson(id, index, 'gender', e.target.value)}
                  >
                    <option value="">Gender</option>
                    {GENDER_CHOICES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                  <input
                    id={fid('age')} className="evb-person__input" type="text" inputMode="numeric"
                    autoComplete="off" placeholder="Age" aria-label={`Person ${n} age`} maxLength={2}
                    aria-invalid={Boolean(problems.age)}
                    value={person.age || ''} disabled={busy}
                    onChange={e => setPerson(id, index, 'age', e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              )}
              <button
                type="button" className="evb-person__more"
                onClick={() => toggleRowMore(moreKey)} disabled={busy}
                aria-expanded={more}
              >
                {more ? '− Gender and age' : '+ Gender and age'}
              </button>
              {startedWithoutName && (
                <p className="evb-person__hint">
                  Add a first name to put this person on the pass.
                </p>
              )}
              {hints.length > 0 && (
                <p className="evb-person__hint">
                  {hints.join(' ')} It won&rsquo;t be saved until it&rsquo;s corrected.
                </p>
              )}
            </div>
          );
        })}

        <p className="evb-people__note" aria-live="polite">{peopleSummary(named, count)}</p>
      </div>
    );
  }

  // Nothing in the list may be touched while a check or a write is running. The
  // check is the new half: a seat count edited while the answer is in flight
  // would be settled against a question nobody asked.
  const busy = phase === WORKING || phase === CHECKING;

  const picked = items.filter(a => seats.has(a.id));
  const totalSeats = picked.reduce((sum, a) => sum + (seats.get(a.id) || 1), 0);

  // Every chosen seat, flattened -- the same shape confirm() sends, so the
  // total shown and the total charged are computed from one list.
  const chosenSeats = picked.flatMap(activity =>
    Array.from({ length: seats.get(activity.id) || 1 }, (_, i) => ({
      activity, seatNo: i + 1,
    })));
  const paidCheckout = isPaidCheckout(chosenSeats);
  const total = checkoutTotal(chosenSeats);

  // What the applied code takes off, and what is left to pay. Zero unless a
  // code has actually been priced by the server -- the page never computes a
  // discount itself, because the server's figure is the one that will be
  // charged.
  const discount = couponApplied ? Number(couponApplied.discount_amount || 0) : 0;

  /**
   * What the gateway will be asked for.
   *
   * The quote's figure the moment there is one, because that is the only one
   * that includes tax, and the button must name the sum that is about to leave
   * the runner's account.
   *
   * FALLS BACK TO THE PRE-TAX FIGURE, NOT TO ZERO AND NOT TO A GUESS. A failed
   * lookup leaves this reading exactly as it read before tax existed. The
   * alternative — holding the Pay button until a tax lookup answers — would
   * let a quote endpoint outage stop every sale on the platform, which is a
   * worse failure than a figure that firms up at the gateway.
   */
  const payable = quoted ? quote.payable : Math.max(total - discount, 0);

  /**
   * The chosen activities as rows for the price breakdown.
   *
   * One row per ACTIVITY with its seat count, not one per seat: three seats on
   * one activity is a quantity, and printing it as three identical lines makes
   * a cart look longer than it is without saying anything more.
   */
  const summaryLines = picked.map(activity => ({
    id: activity.id,
    label: activity.name || 'Entry',
    unit: seatFee({ activity }),
    qty: seats.get(activity.id) || 1,
    amount: seatFee({ activity }) * (seats.get(activity.id) || 1),
  }));

  /**
   * Prices the typed code against the seats currently chosen.
   *
   * Sends the same seat list the checkout will send, so the preview and the
   * charge are answering the same question. A refusal comes back as a normal
   * answer with a reason, which is shown as written for the runner; a null
   * means the call itself failed, which must read as "could not check" rather
   * than as the code being bad.
   */
  async function applyCoupon() {
    const code = couponInput.trim();
    if (!code || couponBusy) return;

    setCouponBusy(true);
    setCouponNote('');
    const answer = await validateCoupon({
      couponCode: code,
      // The event id travels with every seat, because a discount may be
      // scoped to one event and the preview has to answer the same question
      // the charge will. Sent as the PARENT: the registration itself does
      // not exist yet on this path, so the event is the only id there is.
      items: chosenSeats.map(seat => ({
        amount: String(seatFee(seat)),
        chargeable_entity_code: REGISTRATION_ENTITY_CODE,
        chargeable_entity_parent_id: eventId,
      })),
    });
    setCouponBusy(false);

    if (!answer) {
      setCouponNote('We could not check that code just now. Please try again.');
      return;
    }
    if (!answer.applicable) {
      setCouponApplied(null);
      setCouponNote(couponRefusal(answer.reason));
      return;
    }
    setCouponApplied({ ...answer, code });
    appliedForRef.current = cartSignature(seats);
    setCouponNote('');
  }



  function clearCoupon() {
    setCouponApplied(null);
    setCouponNote('');
    setCouponInput('');
  }

  // One rule, per country, shared with the field's own message — so what
  // disables the button and what the runner is told can never disagree.
  const phoneCheck = validateNumber(countryCode, mobileNumber);
  const phoneOk = !paidCheckout || phoneCheck.valid;
  // Complain once they have typed, or once they pressed Pay without a number.
  const showPhoneProblem = !phoneCheck.valid && (Boolean(mobileNumber) || phoneAsked);
  const phoneProblem = mobileNumber ? phoneCheck.reason : 'Mobile number is required.';

  /**
   * Settles one selection against what the check said is left.
   *
   * Returns the selection that CAN be written, plus a sentence naming what had
   * to change. An activity the check said nothing about is carried through
   * untouched — the whole point of the undefined case.
   *
   * WHY IT TRIMS RATHER THAN REFUSES. Seven seats on an activity with three is
   * not a mistake to be sent back; it is a request that is mostly grantable.
   * Cutting it to three and saying so leaves the visitor one press from the
   * entry they actually wanted, where an error would leave them re-reading a
   * form to work out which number offended.
   */
  function settleAgainst(current, left) {
    const next = new Map();
    const emptied = [];
    const cut = [];

    current.forEach((count, id) => {
      const activity = items.find(a => a.id === id);
      if (!left.has(id)) { next.set(id, count); return; }

      const ceiling = seatCeiling(activity, left.get(id));
      const name = activity?.name || 'This activity';
      if (ceiling <= 0) { emptied.push(name); return; }

      const allowed = Math.min(count, ceiling);
      if (allowed !== count) cut.push({ name, allowed, asked: count });
      next.set(id, allowed);
    });

    const parts = [
      ...emptied.map(name => `${name} has no places left`),
      ...cut.map(c => `${c.name} has ${c.allowed} left of the ${c.asked} you asked for`),
    ];

    return {
      seats: next,
      changed: parts.length > 0,
      notice: parts.length === 0 ? null
        : `${parts.join('. ')}. Nothing has been charged — change anything you like and continue.`,
    };
  }

  /**
   * Asks what is left and settles the cart against it.
   *
   * Returns true when the write should go ahead — which is every case except
   * the one where something actually had to change. "Could not tell" goes
   * ahead, because the alternative is a check outage that stops an event
   * selling; and it is recorded as checked so a second press does not sit
   * through the same failing round trip.
   */
  async function settleAvailability(runId) {
    setPhase(CHECKING);
    setTrimNotice(null);

    let left = null;
    try {
      left = await onCheckAvailability(picked);
    } catch (error) {
      // A check that broke is not a refusal. The server still has the last word
      // on every seat, exactly as it did before this call existed.
      left = null;
    }
    if (runId !== runIdRef.current) return false;

    if (!(left instanceof Map) || left.size === 0) {
      verifiedRef.current = cartSignature(seats);
      return true;
    }

    const settled = settleAgainst(seats, left);

    // NOTHING LEFT AT ALL IS NOT THIS CHECK'S CALL TO MAKE.
    //
    // This is the one answer that must go to the server rather than being
    // acted on here, and the reason is not politeness about who decides.
    // "No places left" counts places held by checkouts in progress, and some
    // of those buyers have gone -- closed the tab on the payment page and
    // never came back. Nothing in this browser can know that. The SERVER can:
    // when it is asked for places it cannot give, it asks the gateway whether
    // those older holds are being paid for, releases the ones that are not,
    // and then answers. Refusing here means it is never asked, so those
    // places stay held until they expire on their own -- and a buyer who
    // could have had them is turned away by a count that was already stale.
    //
    // So a cart that has been emptied completely is sent as it was asked for.
    // The worst case is one request that comes back refused, carrying the
    // count the server measured under its own lock, which is better than the
    // count read here a moment ago. ONLY the all-or-nothing case: a cart that
    // was merely trimmed still stops, because the runner is then about to pay
    // a different amount for a different selection and has to see that first.
    const nothingLeftAtAll = settled.changed
      && [...settled.seats.values()].every(count => !count);
    if (nothingLeftAtAll) {
      verifiedRef.current = cartSignature(seats);
      setAvailability(left);
      return true;
    }

    // The settled cart is what was just verified — not the one that was asked
    // for. Signing the asked-for cart would make the next press re-check a
    // selection that no longer exists.
    verifiedRef.current = cartSignature(settled.seats);

    // THE SEATS GO FIRST, THE COUNTS SECOND, AND THE ORDER IS LOAD-BEARING.
    // These are two updates, and outside React's batching — this runs after an
    // await — they paint as two frames. Counts first gives a frame where a full
    // activity is drawn dead but still ticked, which is a row contradicting
    // itself. Seats first gives a frame where it is simply unticked, which is
    // where it is about to end up anyway.
    if (settled.changed) setSeats(settled.seats);
    setAvailability(left);

    if (!settled.changed) return true;
    // The cart changed, so the key minted for the old one would charge the old
    // total. Same rule the steppers follow.
    forgetKey();
    setTrimNotice(settled.notice);
    setPhase(CHOOSE);
    return false;
  }

  async function confirm() {
    if (picked.length === 0 || !agreed) return;
    // Pay is not disabled for a missing number: the press is what explains it.
    if (!phoneOk) {
      setPhoneAsked(true);
      mobileRef.current?.focus();
      return;
    }
    setFatal(null);

    // The check sits between the press and the write, and only when it has
    // something new to ask: a cart already settled goes straight through.
    if (onCheckAvailability && verifiedRef.current !== cartSignature(seats)) {
      const runId = runIdRef.current;
      const proceed = await settleAvailability(runId);
      if (!proceed || runId !== runIdRef.current) return;
    }

    setPhase(WORKING);

    // Minted here, once, and reused for every seat of this press. A retry of
    // the same press carries the same id and is refused by the database.
    const checkoutId = newCheckoutId();
    const requests = [];
    picked.forEach(activity => {
      const count = seats.get(activity.id) || 1;
      const rows = collectParticipants && count > 1 ? (participants.get(activity.id) || []) : [];
      for (let seat = 1; seat <= count; seat += 1) {
        // Only a row with a first name names anybody; see attendeeInfoOf.
        const attendee = attendeeInfoOf(rows[seat - 1]);
        requests.push({
          activity,
          seatNo: seat,
          seatsForActivity: count,
          checkoutId,
          ...(attendee ? { attendee } : {}),
        });
      }
    });

    try {
      // The key and contact details ride alongside the seats rather than
      // inside them: they describe the PAYMENT, not any one seat.
      const outcome = await onConfirm(requests, {
        idempotencyKey: keyRef.current,
        paid: paidCheckout,
        customer: paidCheckout ? {
          customer_phone_country_code: countryCode.trim(),
          customer_phone_national: mobileNumber.replace(/\D/g, ''),
        } : null,
        // Only ever the code the server already priced, never the raw box --
        // sending an unchecked string would charge a figure the runner was
        // not shown. Undefined on a free checkout and when nothing was
        // applied, which leaves the request exactly as it was.
        couponCode: paidCheckout && couponApplied ? couponApplied.code : undefined,
      });
      // A paid checkout hands the runner to the gateway and never comes back
      // here -- the page navigates away mid-await. An empty outcome therefore
      // means "we left", not "nothing happened", and must not flip to a report
      // that would flash a blank result list on the way out.
      if (outcome === undefined) return;
      setResults(outcome || []);
      setPhase(REPORT);
    } catch (error) {
      // A throw here is ours, not the server's — the report has nothing to show,
      // so the modal says what happened and leaves the choice intact.
      setFatal(error?.message || 'Something went wrong before anything was sent.');
      setPhase(CHOOSE);
    }
  }

  /**
   * One line per ACTIVITY, not per seat.
   *
   * Ten seats produced ten identical lines saying "You are registered." — a wall
   * of the same sentence that hid the one line that mattered when a seat failed.
   * Seats that succeeded are counted; seats that failed are listed individually,
   * because each carries its own reason and its own decision for the visitor.
   */
  const grouped = [];
  results.forEach(entry => {
    const key = entry.name;
    let group = grouped.find(g => g.name === key);
    if (!group) {
      group = { name: key, okSeats: [], failures: [] };
      grouped.push(group);
    }
    if (entry.result?.ok) group.okSeats.push(entry.seatNo);
    else group.failures.push(entry);
  });

  const saved = results.filter(r => r.result?.ok);

  // Only entries that actually produced one. A registration whose pass was
  // withheld (a waiting list) is a success with nothing to show at a gate.
  const passes = saved
    .filter(r => r.result?.reveal)
    .map(r => ({
      // activityName: r.seatsForActivity > 1 ? `${r.name} · seat ${r.seatNo}` : r.name,
      activityName: r.attendeeName
        ? `${r.attendeeName} · ${r.name}`
        : (r.seatsForActivity > 1 ? `${r.name} · seat ${r.seatNo}` : r.name),
      // Whatever the button chose to reveal — the field names are configuration,
      // not a fixed contract, so they are carried across as they arrive.
      fields: r.result.reveal.fields || [],
      qrSvg: r.result.reveal.qr?.svg,
    }));
  const refused = results.filter(r => !r.result?.ok);
  const needsSignIn = refused.some(r => r.result?.requiresAuth);

  return createPortal((
    <div className="evb evb-portal">
    <div className="evb-modal" role="presentation"
         onClick={() => { if (phase !== WORKING) onClose(); }}>
      <div
        className="evb-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="evb-modal-title"
        tabIndex={-1}
        ref={panelRef}
        onClick={event => event.stopPropagation()}
      >
        <div className="evb-modal__head">
          <div className="evb-modal__headtext">
            <p className="evb-modal__k">You are entering</p>
            {/* Clamped to two lines in CSS; the tooltip shows the rest. */}
            <h2 className="evb-modal__t" id="evb-modal-title" title={eventTitle || undefined}>
              {eventTitle}
            </h2>
          </div>
          <button
            type="button"
            className="evb-modal__x"
            onClick={onClose}
            disabled={phase === WORKING}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {phase === REPORT ? (
          <div className="evb-modal__body">
            <ul className="evb-report">
              {grouped.map(group => (
                <React.Fragment key={group.name}>
                  {group.okSeats.length > 0 && (
                    <li className="evb-report__row">
                      <span className="evb-report__mark" aria-hidden="true">✓</span>
                      <div>
                        <b>{group.name}</b>
                        <span className="evb-report__msg">
                          {group.okSeats.length === 1
                            ? 'Entered — 1 seat'
                            : `Entered — ${group.okSeats.length} seats`}
                        </span>
                      </div>
                    </li>
                  )}

                  {group.failures.map(entry => (
                    <li className="evb-report__row evb-report__row--bad" key={entry.id}>
                      <span className="evb-report__mark" aria-hidden="true">!</span>
                      <div>
                        <b>
                          {group.name}
                          {entry.seatsForActivity > 1 && (
                            <span className="evb-report__seat">
                              {' '}· seat {entry.seatNo} of {entry.seatsForActivity}
                            </span>
                          )}
                        </b>
                        <span className="evb-report__msg">{failureText(entry.result)}</span>
                      </div>
                    </li>
                  ))}
                </React.Fragment>
              ))}
            </ul>

            <p className="evb-report__total">
              {saved.length} of {results.length} {results.length === 1 ? 'seat' : 'seats'} entered
            </p>

            {/* The passes themselves, one at a time. Three QR codes side by
                side are three things to scan by mistake. */}
            <EventPassViewer passes={passes} eventTitle={eventTitle} />

            {/* Only with a pass: a waiting-list seat has nothing to check in. */}
            {checkInHint && passes.length > 0 && (
              <p className="evb-modal__note evb-modal__note--checkin" role="status">
                {checkInHint}
              </p>
            )}

            {refused.length > 0 && saved.length > 0 && (
              <p className="evb-modal__note">
                The entries marked ✓ are saved. Only the ones marked ! need another try.
              </p>
            )}
            {needsSignIn && (
              <p className="evb-modal__note">Sign in and your choices will be waiting.</p>
            )}
          </div>
        ) : (
          <div className="evb-modal__body">
            {/*
              What the check had to change, before the list it changed. role
              status rather than alert: it is the outcome of something the
              visitor asked for, not an error they made, and a polite
              announcement is what a screen reader should give it.
            */}
            {trimNotice && (
              <p className="evb-modal__note evb-modal__note--trim" role="status">
                {trimNotice}
              </p>
            )}

            {/*
              NOTHING TO TICK IS ITS OWN SCREEN. An event whose activities are
              not published yet reaches here with an empty list, and the
              instruction above then asks the visitor to choose from nothing --
              a pop-up that opens, explains itself, and offers no way forward
              reads as a page that failed to load rather than an event that is
              not ready.

              It says what is missing and whose move it is, because neither is
              the visitor's fault and nothing they do on this screen will
              change it.
            */}
            {items.length === 0 ? (
              <p className="evb-modal__lead" role="status">
                This event has no activities to book yet. The organiser has not
                published any, so there is nothing to choose from — please check
                back later.
              </p>
            ) : (
            <>
            <p className="evb-modal__lead">
              {items.length === 1
                ? 'Confirm the activity you are entering, and how many places you need.'
                : 'Tick every activity you want to enter, and how many places you need for each. Every place is its own entry with its own pass.'}
            </p>

            <ul className="evb-picks">
              {items.map(activity => {
                const id = `evb-pick-${activity.id}`;
                const isOn = seats.has(activity.id);
                const left = leftFor(activity);
                const known = Number.isFinite(left);
                const full = known && left <= 0;
                const ceiling = ceilingFor(activity);
                const headroom = headroomFor(activity);
                /*
                 * THE BOOKING IS FULL, THIS ACTIVITY IS NOT. A different state
                 * from `full` and it has to be, because it is the only one the
                 * visitor can clear themselves.
                 *
                 * Only when it is not already ticked: an activity that is part
                 * of the cart must stay unticked-able, or somebody who filled
                 * the booking could no longer change their mind about it.
                 *
                 * Before this, the checkbox was left enabled and `toggle`
                 * refused the click on its own -- so the row looked available,
                 * did nothing when pressed, and said nothing about why.
                 */
                const cartFull = !isOn && headroom <= 0;
                return (
                  <li key={activity.id}>
                    <div className={`evb-pick${isOn ? ' evb-pick--on' : ''}${full || cartFull ? ' evb-pick--full' : ''}`}>
                      <label className="evb-pick__choose" htmlFor={id}>
                        <input
                          type="checkbox"
                          id={id}
                          checked={isOn}
                          disabled={busy || full || cartFull}
                          onChange={() => toggle(activity.id)}
                        />
                        <span className="evb-pick__body">
                          <b title={activity.name || undefined}>{activity.name}</b>
                          <span className="evb-pick__meta">
                            {activity.fee_amount !== null && activity.fee_amount !== undefined
                              && activity.fee_amount !== ''
                              ? (Number(activity.fee_amount) > 0
                                  ? formatFee(activity.fee_amount)
                                  : 'Free')
                              : null}
                            {activity.max_participants
                              ? ` · ${Number(activity.max_participants).toLocaleString()} places`
                              : ''}
                          </span>

                          {/*
                            The size of the room is above; this is the number of
                            empty chairs, and it only appears once somebody has
                            actually counted them. Three tones, because the three
                            readings need different things from the visitor:
                            none left is a dead end, a handful left is a reason
                            to hurry, and plenty left is a reason to relax.
                          */}
                          {/*
                            THE BOOKING'S BUDGET, NOT THE ACTIVITY'S, and it
                            takes precedence over the availability badge
                            because it is the reason this row cannot be
                            chosen. It is also the only one of the two the
                            visitor can act on, so it says what to do rather
                            than only what is wrong -- the number it is up
                            against is already in the footer.

                            It is shown whether or not the availability check
                            has answered: this limit is ours and is known
                            without asking anybody.
                          */}
                          {cartFull ? (
                            <span className="evb-left evb-left--few">
                              Booking full — remove a seat to add this
                            </span>
                          ) : known && (
                            <span className={`evb-left${full ? ' evb-left--none'
                              : (left <= FEW_LEFT ? ' evb-left--few' : ' evb-left--many')}`}>
                              {full
                                ? 'No places left'
                                : (left <= FEW_LEFT
                                    ? `Only ${left} ${left === 1 ? 'place' : 'places'} left`
                                    : `${left.toLocaleString()} places still open`)}
                            </span>
                          )}
                        </span>
                      </label>

                      {/* Only once it is chosen: a stepper on an unticked row
                          invites setting a number that counts for nothing. */}
                      {isOn && (
                        <div className="evb-seats">
                          <button
                            type="button"
                            className="evb-seats__btn"
                            onClick={() => bumpSeats(activity.id, -1)}
                            disabled={busy || (seats.get(activity.id) || 1) <= 1}
                            aria-label={`One seat fewer for ${activity.name}`}
                          >
                            −
                          </button>
                          <span className="evb-seats__n" aria-live="polite">
                            {seats.get(activity.id) || 1}
                            <span className="evb-seats__word">
                              {(seats.get(activity.id) || 1) === 1 ? ' seat' : ' seats'}
                            </span>
                          </span>
                          <button
                            type="button"
                            className="evb-seats__btn"
                            onClick={() => bumpSeats(activity.id, +1)}
                            disabled={busy
                              || (seats.get(activity.id) || 1) >= ceiling}
                            aria-label={`One seat more for ${activity.name}`}
                          >
                            +
                          </button>

                          {/* A stopped stepper says why. Silence reads as a
                              broken button — and which of the three reasons it
                              stopped for is the difference between "this event
                              is nearly gone" and "come back with a second
                              booking". */}
                          {(seats.get(activity.id) || 1) >= ceiling && (
                            <span className="evb-seats__cap">
                              {/*
                                FOUR REASONS A STEPPER STOPS, and the visitor
                                needs to tell them apart: the event is nearly
                                gone, this activity is small, this activity's
                                own limit is reached, or the rest of the
                                booking has spent the budget. Only the last is
                                fixed by taking a seat off something else, so
                                it must not read like any of the others.

                                Each is matched against the ceiling it
                                produced rather than inferred from what is
                                left over, so adding a fifth limit later
                                cannot silently borrow another one's wording.
                              */}
                              {known && left === ceiling
                                ? `all ${left} left`
                                : (ceiling === capacityOf(activity)
                                    ? `all ${ceiling} places`
                                    : (ceiling === headroom && headroom < SEATS_PER_ACTIVITY
                                        ? `${cartCap} seats per booking`
                                        : `${SEATS_PER_ACTIVITY} per activity`))}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Who the seats are for — optional, closed until asked
                          for, and only where there is more than one seat. One
                          seat is the booker, as it always was. */}
                      {collectParticipants && isOn && (seats.get(activity.id) || 1) > 1 && (
                        <div className="evb-people__slot">
                          {renderPeople(activity, seats.get(activity.id))}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            </>
            )}

            {/*
              Asked only when there is something to pay for. A free entry has
              no reason to want a phone number, and a field between a runner
              and a free event is a field that loses entries.

              Prefilled from the account when it has one, and freely editable:
              the number that matters is the one reachable on the device in
              their hand right now, which is not always the one typed at
              signup. Nothing here is written back to the account.
            */}
            {/*
              The code is entered HERE, before the gateway. Cashfree is handed
              a final figure and knows nothing about coupons, so after the
              redirect there is nothing left to discount.
            */}
            {/*
              The breakdown, ABOVE the coupon box and the contact field.
              A runner reads down to the button; the charge has to be explained
              before the fields that alter it, not discovered underneath them.

              Only where there is something to pay. A free entry has no price
              to detail, and a summary reading "Total payable ₹0.00" is a row
              of noise between a runner and a free event.
            */}
            {paidCheckout && (
              <CheckoutSummary
                lines={summaryLines}
                quote={quoted ? quote : null}
                total={total}
                discount={discount}
                couponCode={couponApplied ? couponApplied.code : null}
                // Says "confirmed at payment" rather than printing nothing,
                // so a lookup that did not answer cannot read as a promise of
                // a tax-free total.
                taxUnknown={!quoted}
                busy={quoteBusy}
                formatFee={formatFee}
              />
            )}

            {paidCheckout && (
              <div className="evb-coupon">
                <label className="evb-coupon__lead" htmlFor="evb-coupon-code">
                  Have a discount code?
                </label>
                <div className="evb-coupon__row">
                  <input
                    id="evb-coupon-code"
                    className="evb-coupon__input"
                    type="text"
                    autoComplete="off"
                    spellCheck="false"
                    placeholder="Enter code"
                    // CouponCode.code is CharField(64); a longer entry could
                    // only ever miss, so the field stops where the column does.
                    maxLength={64}
                    value={couponInput}
                    disabled={Boolean(couponApplied)}
                    onChange={(e) => {
                      // Editing the box drops any applied discount: the
                      // figure on screen must always belong to the code in
                      // the box.
                      setCouponInput(e.target.value.toUpperCase());
                      if (couponApplied) setCouponApplied(null);
                      if (couponNote) setCouponNote('');
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
                  />
                  {couponApplied ? (
                    <button type="button" className="evb-coupon__btn" onClick={clearCoupon}>
                      Remove
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="evb-coupon__btn"
                      onClick={applyCoupon}
                      disabled={couponBusy || !couponInput.trim()}
                    >
                      {couponBusy ? 'Checking…' : 'Apply'}
                    </button>
                  )}
                </div>
                {couponApplied && (
                  <p className="evb-coupon__hint evb-coupon__hint--good" role="status">
                    {`${couponApplied.code} applied — you save ${formatFee(discount)}`}
                    {/* The ceiling, stated while it still matters. Without
                        it a runner adding seats watches the saving stop
                        growing and reads it as a bug. */}
                    {couponApplied.max_discount_amount && (
                      <span className="evb-coupon__cap">
                        {` (up to ${formatFee(couponApplied.max_discount_amount)} on this offer)`}
                      </span>
                    )}
                  </p>
                )}
                {couponNote && (
                  <p className="evb-coupon__hint evb-coupon__hint--bad" role="alert">
                    {couponNote}
                  </p>
                )}
              </div>
            )}

            {paidCheckout && (
              <div className="evb-contact">
                <p className="evb-contact__lead">
                  Your mobile number
                </p>
                <div className="evb-contact__row">
                  <span className="evb-contact__field evb-contact__field--code">
                    <label htmlFor="evb-country-code">Country</label>
                    {/*
                      Fixed at +91, not chosen. Entries are taken in India only,
                      so the dial code is a fact rather than a decision, and a
                      picker with one right answer is a step that can only be
                      got wrong. Shown rather than hidden so the number on
                      screen is the whole number we will text.
                    */}
                    <input
                      id="evb-country-code"
                      className="evb-contact__input"
                      type="text"
                      disabled
                      readOnly
                      value={countryCode}
                    />
                  </span>

                  <span className="evb-contact__field">
                    <label htmlFor="evb-mobile-number">
                      Mobile number
                      {/* A paid checkout will not submit without it, so the
                          field is marked rather than leaving the runner to
                          discover it from a disabled Pay button. */}
                      <span className="evb-req" aria-hidden="true">*</span>
                      <span className="evb-sr">(required)</span>
                    </label>
                    <input
                      id="evb-mobile-number"
                      ref={mobileRef}
                      className="evb-contact__input"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      // The country's own length, so the field cannot hold a
                      // number that could never be valid behind this code.
                      maxLength={normaliseNumber(dialCodeEntry(countryCode).sample).length}
                      placeholder={dialCodeEntry(countryCode).sample}
                      value={mobileNumber}
                      disabled={busy}
                      aria-invalid={showPhoneProblem}
                      aria-describedby="evb-phone-hint"
                      // Digits only, at the point of entry. Stripping on submit
                      // instead would let somebody type a space and watch it
                      // vanish later, which reads as the field eating input.
                      onChange={e => setMobileNumber(normaliseNumber(e.target.value))}
                    />
                  </span>
                </div>

                {/*
                  Only complains once they have typed or pressed Pay — a
                  complaint on an untouched field reads as a broken form.
                  Silent and reassuring when it is right, so the hint is not a
                  permanent warning.
                */}
                <p
                  id="evb-phone-hint"
                  className={`evb-contact__hint${
                    showPhoneProblem ? ' evb-contact__hint--bad' : ''}`}
                  role={showPhoneProblem ? 'alert' : undefined}
                >
                  {showPhoneProblem
                    ? phoneProblem
                    : `We may use this number to reach out to you to sort out anything related to your registrations. ${dialCodeEntry(countryCode).hint}.`}
                </p>
              </div>
            )}

            {/* Not a <label>: only the box itself toggles, so tapping the
                sentence (e.g. to read it) never ticks it by accident. The
                text still names the box via aria-labelledby. */}
            <div className="evb-consent">
              <input
                id="evb-consent"
                type="checkbox"
                checked={agreed}
                disabled={busy}
                required
                aria-labelledby="evb-consent-text"
                onChange={e => setAgreed(e.target.checked)}
              />
              <span id="evb-consent-text">
                {CONSENT_TEXT}
                <span className="evb-req" aria-hidden="true">*</span>
                <span className="evb-sr">(required)</span>
              </span>
            </div>

            {fatal && <p className="evb-modal__error" role="alert">{fatal}</p>}
          </div>
        )}

        <div className="evb-modal__foot">
          {phase === REPORT ? (
            <button type="button" className="evb-modal__go" onClick={onClose}>Done</button>
          ) : (
            <>
              <span className="evb-modal__count">
                {picked.length === 0
                  ? 'Nothing selected'
                  : (
                    <>
                      {`${totalSeats} ${totalSeats === 1 ? 'seat' : 'seats'} · ${picked.length} ${picked.length === 1 ? 'activity' : 'activities'}`}
                      {/* Only once the cart's own ceiling is what is stopping
                          the steppers. Stating "of 75" while every stepper is
                          still held back by its activity's capacity would name
                          a budget nobody is spending, and invite a visitor to
                          go looking for seats that are not there. */}
                      {totalSeats >= cartCap && (
                        <span className="evb-modal__cap">{` · ${cartCap} per booking`}</span>
                      )}
                      {/* The total is stated before the button that charges
                          it, and computed from the same seat list the
                          checkout sends -- so what is shown and what is
                          charged cannot drift apart. */}
                      {/* STRUCK THROUGH ONLY WHEN THE TOTAL WENT DOWN.
                          The old rule keyed on whether a coupon was applied,
                          which was the same question while a discount was the
                          only thing that could move the figure. Tax moves it
                          UP, and striking out the smaller number to show a
                          bigger one reads as a price rise dressed as a saving.
                          So it now keys on the direction of travel, which is
                          what the strike-through actually means. */}
                      {paidCheckout && (
                        payable < total ? (
                          <>
                            {' · '}
                            <s className="evb-modal__was">{formatFee(total)}</s>
                            {' '}
                            <b className="evb-modal__total">{formatFee(payable)}</b>
                          </>
                        ) : (
                          <b className="evb-modal__total"> · {formatFee(payable)}</b>
                        )
                      )}
                    </>
                  )}
              </span>
              <button
                type="button"
                className="evb-modal__go"
                onClick={confirm}
                disabled={busy || picked.length === 0 || !agreed
                  || (paidCheckout && !keyReady)}
              >
                {busy
                  ? (<><span className="evb-spin" aria-hidden="true" />
                      {phase === CHECKING
                        ? 'Checking availability…'
                        : (paidCheckout ? 'Taking you to payment…' : 'Entering…')}</>)
                  : (paidCheckout ? `Pay ${formatFee(payable)}` : 'Continue to checkout')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    </div>
  ), document.body);
}
